/* eslint-disable no-nested-ternary */
/* eslint-disable camelcase */
// useEventForm.js
import { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import moment from 'moment';
import _ from 'lodash';
import axios from 'axios';
import { useAddEventMutation, useGetEventDetailsQuery } from 'store/services/schedule/events';
import { useGetServicesQuery } from 'store/services/common';
import usePermission from 'hooks/usePermission';
import useAuth from 'hooks/useAuth';
import { PAYLOAD_DATE_TIME_FORMAT, THERAPY_MIN_TIME, THERAPY_MAX_TIME } from 'constant';
import { isDateValid } from 'utils/validation';

// constant
const EventSchema = Yup.object().shape({
    title: Yup.string().max(255),
    description: Yup.string().max(5000).nullable(),
    // end: Yup.date().when('start', (start, schema) => start && schema.min(start, '*End date must be later than start date')),
    start: Yup.date()
        .required()
        .test('is-valid-date', 'Please Enter a Valid Date', (value) => isDateValid(value))
        .typeError('Please enter a valid date'),
    hour: Yup.number().required('*Please select a hour.'),
    minute: Yup.number().required('*Please select a minute'),
    visitType: Yup.string().required('*Please select a visit type.'),
    status: Yup.string(),
    service: Yup.object()
        .shape({
            id: Yup.number().required('*Select a valid service type.'),
            service_name: Yup.string().required('*Select a valid service type.')
        })
        .required('*Select a valid service type.')
        .nullable(),
    employee: Yup.object().nullable().required('*Select a valid Employee.'),
    patient: Yup.object()
        .shape({
            id: Yup.number().required('*Please select a Patient.'),
            name: Yup.string().required('*Please select a Patient.')
        })
        .nullable()
        .when('treatment_type', {
            is: (value) => value !== 'group', // Required only if treatment_type is NOT 'group'
            then: (schema) => schema.required('*Please select a Patient.'),
            otherwise: (schema) => schema.notRequired().nullable()
        }),
    location: Yup.object()
        .shape({
            id: Yup.number().required('*Select a valid location.'),
            name: Yup.string().required('*Select a valid location.')
        })
        .required('*Select a valid location.')
        .nullable(),
    color: Yup.string().max(255).nullable(),
    textColor: Yup.string().max(255).nullable(),
    treatment_type: Yup.string(),
    grp_patients: Yup.array()
        .of(Yup.object())
        .nullable()
        .when('treatment_type', {
            // eslint-disable-next-line arrow-body-style
            is: (value) => {
                return value === 'group';
            },
            then: Yup.array()
                .of(Yup.object())
                .min(2, 'Please select at least two Patients.')
                .max(4, 'Maximun of 4 patients allowed.')
                .required('Please select at least two Patients.')
                .typeError('Please select at least two Patients.')
        }),

    eventType: Yup.string().required('*Please select a reccurence option.').typeError('*Please select a reccurence option.'),
    // group_schedule: Yup.mixed().nullable(),
    groupSchedule: Yup.mixed().nullable(),
    rec_end_type: Yup.string().required('*Please select an end date for the recurrence.'),
    // noOfOccurence: Yup.string().when(['rec_end_type', 'group_schedule'], {
    //     is: (rec_end_type, groupSchedule) => rec_end_type === 'no_of_occurances' && groupSchedule,
    //     then: Yup.string()
    //         .required('*Please select a valid occurrence.')
    //         .test('is-valid-range', '*Please select a valid occurrence between 1 and 99.', (value) => {
    //             const num = Number(value);
    //             return value !== null && value !== '' && Number.isInteger(num) && num >= 1 && num <= 99;
    //         }),
    //     otherwise: Yup.string().nullable().notRequired()
    // }),
    noOfOccurence: Yup.string().when(['rec_end_type', 'groupSchedule'], {
        is: (rec_end_type, groupSchedule) => rec_end_type === 'no_of_occurances' && groupSchedule === '1',
        then: Yup.string()
            .required('*Please select a valid occurrence.')
            .test('is-valid-range', '*Please select a valid occurrence between 1 and 99.', (value) => {
                const num = Number(value);
                return value !== null && value !== '' && Number.isInteger(num) && num >= 1 && num <= 99;
            }),
        otherwise: Yup.string().nullable().notRequired()
    }),
    endDateTime: Yup.date().when(['rec_end_type', 'groupSchedule'], {
        is: (rec_end_type, groupSchedule) => rec_end_type === 'end_date' && groupSchedule === '1',
        then: Yup.date()
            .nullable()
            .required('*Please enter a valid date')
            .test('is-valid-date', 'Please Enter a Valid Date', (value) => isDateValid(value))
            .test('within-range', function (value) {
                const { start, eventType } = this.parent;
                if (!value || !start) return true;

                const startDate = new Date(start);
                const endDate = new Date(value);

                let maxEndDate;
                let rangeLabel;

                switch (eventType) {
                    case 'monthly':
                        maxEndDate = new Date(startDate);
                        maxEndDate.setMonth(startDate.getMonth() + 12);
                        rangeLabel = 'within 12 months';
                        break;
                    case 'yearly':
                        maxEndDate = new Date(startDate);
                        maxEndDate.setFullYear(startDate.getFullYear() + 5);
                        rangeLabel = 'within 5 years';
                        break;
                    default:
                        maxEndDate = new Date(startDate);
                        maxEndDate.setDate(startDate.getDate() + 99);
                        rangeLabel = 'within 99 days';
                }

                const isValidRange = endDate >= startDate && endDate <= maxEndDate;

                return (
                    isValidRange ||
                    this.createError({
                        message: `End date must be ${rangeLabel} from the start date`
                    })
                );
            })
            .typeError('Please enter a valid date'),
        otherwise: Yup.date().nullable()
    }),
    weekDays: Yup.array().when(['eventType', 'groupSchedule'], {
        is: (eventType, groupSchedule) => eventType === 'weekly' && groupSchedule === '1',
        then: Yup.array().min(1, '*Select at least one day').required('*Please select at least one day'),
        otherwise: Yup.array().notRequired()
    }),
    monthly_rec_type: Yup.string().when(['eventType', 'groupSchedule'], {
        is: (eventType, groupSchedule) => eventType === 'monthly' && groupSchedule === '1',
        then: Yup.string().required('*Please select a valid repeat frequency.'),
        otherwise: Yup.string().notRequired()
    }),
    event_nature: Yup.string().nullable()
});

const getInitialValues = (event, range) => {
    const newEvent = {
        title: '',
        description: '',
        color: '#2196f3',
        textColor: '',
        allDay: false,
        status: '',
        days: [],
        visitType: event ? event.visit_type : '',
        employee: !event ? null : event.employees?.[0],
        // eslint-disable-next-line consistent-return
        patient: (() => {
            if (!event) {
                return null;
            }
            if (!event.is_grouped) {
                const patient = event.patients?.[0];
                if (patient) {
                    return { ...patient, default_name: patient.p_name || patient.name };
                }
                return null;
            }
            return null;
        })(),
        hour: event ? event.duration_hour : 1,
        minute: event ? event.duration_minute : 0,
        location: event ? event.locations?.[0] : null,
        service: event ? event.services?.[0] : null,
        start: (() => {
            if (event) {
                return moment.utc(event.start_date_time).local();
            }
            if (range) {
                // return moment(new Date(range.start)).add(6, 'hours');
                return moment(new Date(range.start));
            }
            return null;
        })(),
        end: (() => {
            if (event) {
                return moment.utc(event.endDateTime).local();
            }
            if (range) {
                return moment(new Date(range.end));
            }
            return null;
        })(),
        treatment_type: event && event?.is_grouped ? 'group' : 'individual',
        // eslint-disable-next-line consistent-return
        grp_patients: (() => {
            if (!event) {
                return null;
            }
            // for handling patient dropdown structure
            if (event.is_grouped) {
                return event.patients.map((item) => ({
                    id: item.id,
                    user: {
                        name: item.name,
                        defaultName: item?.p_name
                    }
                }));
            }
            return null;
        })(),

        // ---------- Recurring Schedule ----------------
        groupSchedule: event?.group_schedule && typeof event.group_schedule === 'object' ? '1' : '0',

        eventType: event?.group_schedule ? event?.group_schedule?.occurence.toLowerCase() : 'daily',
        rec_end_type: event?.group_schedule
            ? event?.group_schedule.event === 'NO_OF_OCCURENCE'
                ? 'no_of_occurances'
                : event?.group_schedule?.event.toLowerCase()
            : 'no_of_occurances',
        noOfOccurence: event?.group_schedule ? event?.group_schedule?.no_of_occurence ?? '' : '',
        endDateTime: event?.group_schedule?.end_date ? moment(event?.group_schedule.end_date) : '',
        weekDays: event?.group_schedule
            ? event?.group_schedule?.weekly_repeat_type
                ? JSON.parse(event?.group_schedule?.weekly_repeat_type)
                : []
            : [],
        monthly_rec_type:
            event?.group_schedule?.monthly_repeat_type === 'DAY'
                ? 'dayOfMonth'
                : event?.group_schedule?.monthly_repeat_type === 'WEEKDAY'
                  ? 'weekdayOfMonth'
                  : '',

        week_number: event ? event.weekDays : '',
        monthly_week_day: '',
        monthly_week_number: '',
        event_nature: '',
        group_schedule_id: event?.group_schedule ? event?.group_schedule.id : null
    };

    if (event || range) {
        return _.merge({}, newEvent, event);
    }

    return newEvent;
};

const generateHour = () => {
    const hours = [];
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < 13; i++) {
        hours.push({ value: i, label: i });
    }

    return hours;
};

const generateMinute = () => {
    const minute = [];
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < 61; i += 5) {
        if (i === 60) {
            minute.push({ value: 59, label: 59 });
        } else {
            minute.push({ value: i, label: i });
        }
    }

    return minute;
};

const makeTitle = (service, patient) => {
    let title = '';
    if (service) {
        title += service;
        title += ' for ';
    }

    if (Array.isArray(patient)) {
        if (patient.length > 0) {
            title = `Group ${title}`;
            title += patient.map((pat) => pat.defaultName).join(', ');
        }
    } else if (patient && typeof patient === 'object') {
        title += patient.default_name || '';
    }

    let newTitle = title.toLowerCase();
    newTitle = newTitle.charAt(0).toUpperCase() + newTitle.slice(1);

    return newTitle;
};

const transformData = (array, string, id, nameFormatter = null) => {
    const transformedArray = [];

    array.forEach((item) => {
        let name;
        if (item[string]) {
            name = item[string];
        } else if (nameFormatter) {
            name = nameFormatter(item);
        } else {
            name = [item.first_name, item.middle_name, item.last_name].filter(Boolean).join(' ');
        }
        transformedArray.push({ name, id: item[id], preference: item?.is_preference });
    });

    return transformedArray;
};

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const visitValues = [
    { value: 'SCREENING', label: 'Screening' },
    { value: 'EVALUATION', label: 'Evaluation' },
    { value: 'TREATMENT', label: 'Treatment' }
];

const shotDays = [
    { id: 6, label: 'S', name: 'Sunday' },
    { id: 0, label: 'M', name: 'Monday' },
    { id: 1, label: 'T', name: 'Tuesday' },
    { id: 2, label: 'W', name: 'Wednesday' },
    { id: 3, label: 'T', name: 'Thursday' },
    { id: 4, label: 'F', name: 'Friday' },
    { id: 5, label: 'S', name: 'Saturday' }
];

const treatmentDataKeys = [
    'locationId',
    'serviceId',
    'visitType',
    'employeeId',
    'patientId',
    'treatmentType',
    'description',
    'group_patientId'
];
const startTimeKeys = ['startTime', 'durationHour', 'durationMinute'];

const recChangeKeys = ['eventType', 'noOfOccurence', 'endDateTime', 'weekDays', 'monthly_rec_type'];

export {
    getInitialValues,
    generateHour,
    generateMinute,
    makeTitle,
    transformData,
    EventSchema,
    days,
    visitValues,
    shotDays,
    treatmentDataKeys,
    startTimeKeys,
    recChangeKeys
};
